import React from "react";
import { NavLink } from "react-router-dom";
import {
  LayoutDashboard, Users, FileText, CalendarDays, NotebookPen,
  Settings, Stethoscope, UserPlus, Mail, Building, ShieldCheck,
  DollarSign
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const items = [
  { to: "/app", icon: LayoutDashboard, label: "Dashboard", testid: "nav-dashboard", end: true },
  { to: "/app/pacientes", icon: Users, label: "Pacientes", testid: "nav-patients" },
  { to: "/app/vinculacoes", icon: UserPlus, label: "Vínculos", testid: "nav-vinculacoes" },
  { to: "/app/convites", icon: Mail, label: "Convites", testid: "nav-convites" },
  { to: "/app/agenda", icon: CalendarDays, label: "Agenda", testid: "nav-agenda" },
  { to: "/app/sessoes", icon: NotebookPen, label: "Sessões", testid: "nav-sessions" },
  { to: "/app/clinica", icon: Building, label: "Minha Clínica", testid: "nav-clinic" },
  { to: "/app/financeiro", icon: DollarSign, label: "Financeiro", testid: "nav-financeiro" },
  { to: "/app/configuracoes", icon: Settings, label: "Configurações", testid: "nav-settings" },
  { to: "/admin", icon: ShieldCheck, label: "Painel Admin", testid: "nav-admin" },
];

export default function Sidebar() {
  const { profile, clinic } = useAuth();
  const myRole = clinic?.my_role || "professional";

  const filteredItems = items.filter(item => {
    // Superadmin logic
    if (profile?.role === 'superadmin') return true;
    if (item.to === '/admin') return false; // Hide from everyone else

    // Admin sees everything
    if (myRole === 'admin') return true;

    // Permissions for Receptionist
    if (myRole === 'receptionist') {
      const restricted = ['/app/convites', '/app/clinica', '/app/sessoes', '/app/financeiro'];
      return !restricted.includes(item.to);
    }

    // Permissions for Professional (Team Member)
    if (myRole === 'professional') {
      const restricted = ['/app/convites', '/app/clinica'];
      return !restricted.includes(item.to);
    }

    return true;
  });

  const getItem = (to) => filteredItems.find(i => i.to === to);

  const roleLabel = {
    admin: "Gestor",
    professional: "Equipe",
    receptionist: "Recepção"
  }[myRole] || "Equipe";

  return (
    <aside data-testid="app-sidebar" className="hidden md:flex sidebar-grain w-64 shrink-0 border-r border-[hsl(var(--border-default))] flex-col h-screen sticky top-0">
      <div className="px-5 h-16 flex items-center gap-2.5 border-b border-[hsl(var(--border-divider))]">
        <div className="w-10 h-10 rounded-xl bg-white overflow-hidden flex items-center justify-center shadow-sm p-1.5">
          <img src="/iatros-logo-icon-simple.svg" alt="Iatrós Logo" className="w-full h-full object-contain" />
        </div>
        <div className="leading-tight">
          <div className="text-[15px] font-semibold tracking-tight" style={{fontFamily:"'Outfit',sans-serif"}}>Iatrós</div>
          <div className="text-[11px] text-[hsl(var(--text-tertiary))] -mt-0.5">{clinic?.name || "Clínica"} · {roleLabel}</div>
        </div>
      </div>

      <nav className="flex-1 px-3 py-5 space-y-1 overflow-y-auto" aria-label="Navegação principal">
        <div className="ds-overline px-3 mb-2">Geral</div>
        {['/app', '/app/pacientes'].map(to => getItem(to) && <NavItem key={to} {...getItem(to)} />)}
        
        {['/app/vinculacoes', '/app/convites'].some(to => getItem(to)) && (
          <>
            <div className="ds-overline px-3 mb-2 mt-5">Aquisição</div>
            {['/app/vinculacoes', '/app/convites'].map(to => getItem(to) && <NavItem key={to} {...getItem(to)} />)}
          </>
        )}

        {['/app/agenda', '/app/sessoes'].some(to => getItem(to)) && (
          <>
            <div className="ds-overline px-3 mb-2 mt-5">Atendimento</div>
            {['/app/agenda', '/app/sessoes'].map(to => getItem(to) && <NavItem key={to} {...getItem(to)} />)}
          </>
        )}

        {getItem('/app/clinica') && (
          <>
            <div className="ds-overline px-3 mb-2 mt-5">Clínica</div>
            <NavItem {...getItem('/app/clinica')} />
          </>
        )}

        {getItem('/app/financeiro') && (
          <>
            <div className="ds-overline px-3 mb-2 mt-5">Finanças</div>
            <NavItem {...getItem('/app/financeiro')} />
          </>
        )}

        {getItem('/admin') && (
          <>
            <div className="ds-overline px-3 mb-2 mt-5">Administração</div>
            <NavItem {...getItem('/admin')} />
          </>
        )}

        <div className="ds-overline px-3 mb-2 mt-5">Conta</div>
        {['/app/configuracoes'].map(to => getItem(to) && <NavItem key={to} {...getItem(to)} />)}
      </nav>

      <div className="p-3 border-t border-[hsl(var(--border-divider))]">
        <div className="rounded-lg bg-[hsl(var(--brand))] text-white p-4">
          <div className="text-[11px] font-semibold tracking-[0.08em] uppercase opacity-80">{profile?.full_name?.split(" ")[0] || "Olá"}</div>
          <div className="text-sm mt-1.5 leading-snug">Painel de {roleLabel.toLowerCase()} ativado.</div>
        </div>
      </div>
    </aside>
  );
}

function NavItem({ to, icon: Icon, label, testid, end }) {
  return (
    <NavLink to={to} end={end} data-testid={testid}
      className={({ isActive }) =>
        `flex items-center gap-3 px-3 h-10 rounded-lg text-sm font-medium transition-colors ${
          isActive ? "bg-[hsl(var(--brand))] text-white shadow-sm"
                   : "text-[hsl(var(--text-secondary))] hover:bg-[hsl(var(--border-default))]/60 hover:text-[hsl(var(--text-primary))]"
        }`
      }>
      <Icon className="w-[18px] h-[18px]" />
      <span>{label}</span>
    </NavLink>
  );
}
